import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Users, UserPlus, Trash2, Loader2, Lock, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const roleLabel = (r) => (r === "owner" ? "Owner" : r === "admin" ? "Admin" : "Member");
const statusLabel = (s) => (s === "active" ? "Active" : s === "pending" ? "Pending" : s);

export const PENDING_INVITE_TOKEN_KEY = "pm_pending_invite_token";

export default function Team() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  // null = "all workspaces" (legacy default; the server never persists
  // the field). An ARRAY (even empty) = explicit assignment. This
  // distinction matches the server: missing → all; [] → none.
  const [inviteWorkspaceIds, setInviteWorkspaceIds] = useState(null);
  const [sending, setSending] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  // Teams V2 edit modal: role + per-workspace access for a specific member.
  const [editTarget, setEditTarget] = useState(null); // member row or null
  const [editRole, setEditRole] = useState("member");
  // Same semantics as inviteWorkspaceIds — null means "all", array (even
  // empty) means explicit. Legacy rows arrive as workspace_ids: null on
  // the row so we hydrate to null here.
  const [editWorkspaceIds, setEditWorkspaceIds] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  // Owner's active business workspaces (for the access picker). Fetched
  // lazily so a member's Team page doesn't churn the list endpoint.
  const [ownerWorkspaces, setOwnerWorkspaces] = useState([]);
  const [accepting, setAccepting] = useState(false);
  const [needsSignin, setNeedsSignin] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Read the invite token from the URL. If we get bounced to login and
  // return, the same token is re-read from sessionStorage — the raw
  // token never appears in logs (only its sha256 hash reaches the DB).
  const acceptToken = params.get("token")
    || (typeof window !== "undefined"
        ? window.sessionStorage.getItem(PENDING_INVITE_TOKEN_KEY)
        : null);

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await api.get("/team");
      setState({ loading: false, data: res.data, error: null });
      setNeedsSignin(false);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // Anonymous visitor. If an invite token is in play, defer the
        // roster fetch and route them through auth first.
        setNeedsSignin(true);
        setState({ loading: false, data: null, error: null });
      } else {
        setState({ loading: false, data: null, error: e?.response?.data || e.message });
      }
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Invite-acceptance flow — handles all three cases:
  //   (a) already logged in as invited email → accept and land on /team
  //   (b) existing user logged out → stash token, bounce to /login,
  //       Protected re-hydrates and Navigate(/team/accept) auto-consumes
  //   (c) brand-new user → stash token, /signup, then same auto-consume
  useEffect(() => {
    if (!acceptToken || accepting) return;
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, acceptToken);
    }
    if (needsSignin) {
      navigate(`/login?next=${encodeURIComponent("/team/accept")}`, { replace: true });
      return;
    }
    if (!state.data) return;  // wait until we know we're authenticated
    (async () => {
      setAccepting(true);
      try {
        await api.post("/team/accept", { token: acceptToken });
        toast.success("You've joined the workspace");
        if (typeof window !== "undefined") {
          window.sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
        }
        navigate("/team", { replace: true });
        refresh();
      } catch (e) {
        const detail = e?.response?.data?.detail;
        toast.error(detail?.message || "Invitation could not be accepted");
        if (typeof window !== "undefined") {
          // Clear so the user isn't stuck in a redirect loop on an
          // invalid / expired / revoked token.
          window.sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
        }
      } finally {
        setAccepting(false);
      }
    })();
  }, [acceptToken, accepting, navigate, refresh, needsSignin, state.data]);

  const isOwner = state.data?.role === "owner";
  const seatsUsed = state.data?.seats_used || 0;
  const seatsTotal = state.data?.seats_total || 3;
  const seatsAvailable = seatsTotal - seatsUsed;
  const members = useMemo(() => state.data?.members || [], [state.data]);

  const onInvite = async () => {
    const email = (inviteEmail || "").trim();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setSending(true);
    try {
      // Preserve the null vs [] distinction end-to-end:
      //   * null (all workspaces) → send null; server treats as legacy
      //     "grant every active owner workspace".
      //   * [...] (including [])  → send the array verbatim; server
      //     records it as an explicit assignment, so [] means the
      //     invitee gets zero workspace access until edited.
      await api.post("/team/invite", {
        email,
        role: inviteRole,
        workspace_ids: inviteWorkspaceIds,
      });
      toast.success(`Invitation sent to ${email}`);
      setInviteOpen(false);
      setInviteEmail("");
      setInviteRole("member");
      setInviteWorkspaceIds(null);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || "Could not send invitation");
    } finally {
      setSending(false);
    }
  };

  // Fetch the owner's ACTIVE business workspaces for the access picker.
  // Guarded so we only call it when the caller is the owner AND we
  // actually need to render the picker (invite / edit modal).
  const loadOwnerWorkspaces = useCallback(async () => {
    try {
      const res = await api.get("/business-workspaces");
      const list = (res.data?.workspaces || []).filter((w) => w.status !== "archived");
      setOwnerWorkspaces(list);
    } catch (_e) {
      // Non-fatal: picker falls back to "all workspaces" (legacy default).
      setOwnerWorkspaces([]);
    }
  }, []);

  const onOpenInvite = () => {
    setInviteOpen(true);
    loadOwnerWorkspaces();
  };

  const onEditOpen = (m) => {
    setEditTarget(m);
    setEditRole(m.role === "admin" ? "admin" : "member");
    // Legacy row (workspace_ids null on the server) hydrates to null
    // here ("all workspaces"). An explicit array — including [] —
    // hydrates as-is, preserving the "explicit zero access" state.
    setEditWorkspaceIds(Array.isArray(m.workspace_ids) ? m.workspace_ids.slice() : null);
    loadOwnerWorkspaces();
  };

  const onEditSave = async () => {
    if (!editTarget) return;
    setEditSaving(true);
    try {
      // Send the workspace_ids value verbatim so null vs [] intent is
      // preserved on the server (null = legacy all, [] = deliberate none).
      await api.patch(`/team/members/${editTarget.user_id}`, {
        role: editRole,
        workspace_ids: editWorkspaceIds,
      });
      toast.success("Team member updated");
      setEditTarget(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not update team member");
    } finally {
      setEditSaving(false);
    }
  };

  const setInviteAllWorkspaces = (on) => {
    setInviteWorkspaceIds(on ? null : []);
  };
  const setEditAllWorkspaces = (on) => {
    setEditWorkspaceIds(on ? null : []);
  };
  const toggleInviteWorkspace = (wid) => {
    setInviteWorkspaceIds((prev) => {
      if (prev === null) {
        // Switching from "all" to explicit: start from every workspace,
        // then remove the one the user just unchecked.
        const all = ownerWorkspaces.map((x) => x.workspace_id).filter((x) => x !== wid);
        return all;
      }
      return prev.includes(wid) ? prev.filter((w) => w !== wid) : [...prev, wid];
    });
  };
  const toggleEditWorkspace = (wid) => {
    setEditWorkspaceIds((prev) => {
      if (prev === null) {
        const all = ownerWorkspaces.map((x) => x.workspace_id).filter((x) => x !== wid);
        return all;
      }
      return prev.includes(wid) ? prev.filter((w) => w !== wid) : [...prev, wid];
    });
  };

  const onRemove = async () => {
    if (!removeTarget) return;
    try {
      await api.delete(`/team/members/${removeTarget.user_id}`);
      toast.success(`Removed ${removeTarget.email}`);
      setRemoveTarget(null);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not remove member");
    }
  };

  if (state.loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-neutral-500" data-testid="team-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading team…
      </div>
    );
  }

  // Non-Business plan → upgrade state
  if (state.data && state.data.eligible === false) {
    return (
      <div className="p-8" data-testid="team-upgrade-required">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                <Lock className="w-5 h-5 text-neutral-500" />
              </div>
              <div>
                <CardTitle>Team collaboration is included with the Business plan</CardTitle>
                <CardDescription>
                  Invite up to 2 team members to collaborate in the same Postmoro workspace with individual logins.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button data-testid="team-upgrade-cta" onClick={() => navigate("/billing")}>
              Upgrade to Business
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl" data-testid="team-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <Users className="w-7 h-7" /> Team
          </h1>
          <p className="text-neutral-500 mt-1">
            Invite your team to collaborate in your Postmoro workspace.
          </p>
        </div>
        {isOwner && (
          <Button
            data-testid="team-invite-open-btn"
            onClick={onOpenInvite}
            disabled={seatsAvailable <= 0}
          >
            <UserPlus className="w-4 h-4 mr-2" /> Invite Team Member
          </Button>
        )}
      </div>

      <Card className="mb-6" data-testid="team-seats-card">
        <CardContent className="pt-6 flex items-center gap-3">
          <div className="text-sm text-neutral-500">Team members</div>
          <div className="text-lg font-medium" data-testid="team-seats-usage">
            {seatsUsed} of {seatsTotal} seats used
          </div>
          {seatsAvailable <= 0 && (
            <span className="text-xs text-neutral-500 ml-auto" data-testid="team-seats-full-note">
              Your Business plan includes up to {seatsTotal} team members.
            </span>
          )}
        </CardContent>
      </Card>

      <Card data-testid="team-members-card">
        <CardContent className="pt-6 divide-y">
          {members.map((m, idx) => {
            // Workspace access summary. The server distinguishes:
            //   * m.workspace_ids === null → legacy default = "All workspaces"
            //   * m.workspace_ids === []   → explicit zero access = "None"
            //   * m.workspace_ids array    → count of assigned workspaces
            const accessLabel = m.role === "owner"
              ? "All workspaces"
              : m.workspace_ids === null || m.workspace_ids === undefined
                ? "All workspaces"
                : m.workspace_ids.length === 0
                  ? "None"
                  : `${m.workspace_ids.length} workspace${m.workspace_ids.length === 1 ? "" : "s"}`;
            return (
              <div
                key={m.user_id || m.email || idx}
                className="py-3 flex items-center gap-4"
                data-testid={`team-member-row-${idx}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{m.name || m.email}</div>
                  <div className="text-xs text-neutral-500 truncate">{m.email}</div>
                </div>
                <Badge
                  variant={m.role === "owner" ? "default" : "secondary"}
                  data-testid={`team-member-role-${idx}`}
                >
                  {roleLabel(m.role)}
                </Badge>
                <div
                  className="text-xs text-neutral-500 hidden sm:block min-w-[120px] text-right"
                  data-testid={`team-member-workspace-access-${idx}`}
                >
                  {accessLabel}
                </div>
                <Badge
                  variant={m.status === "active" ? "outline" : "secondary"}
                  data-testid={`team-member-status-${idx}`}
                >
                  {statusLabel(m.status)}
                </Badge>
                {isOwner && m.role !== "owner" && m.user_id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`team-member-edit-${idx}`}
                    onClick={() => onEditOpen(m)}
                    aria-label="Edit team member"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
                {isOwner && m.role !== "owner" && m.user_id && (
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid={`team-member-remove-${idx}`}
                    onClick={() => setRemoveTarget(m)}
                    aria-label="Remove team member"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent data-testid="team-invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation email. The link is single-use and expires in 7 days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              data-testid="team-invite-email-input"
              type="email"
              placeholder="teammate@company.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              disabled={sending}
            />
            <div>
              <div className="text-xs text-neutral-500 mb-1.5">Role</div>
              <div className="flex gap-2" data-testid="team-invite-role-group">
                {[
                  { id: "admin",  label: "Admin",  desc: "Manage settings, connections, Autopilot" },
                  { id: "member", label: "Member", desc: "Create posts, use library, publish" },
                ].map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setInviteRole(r.id)}
                    data-testid={`team-invite-role-${r.id}`}
                    aria-pressed={inviteRole === r.id}
                    className={
                      "flex-1 text-left rounded-md border p-3 text-xs transition " +
                      (inviteRole === r.id
                        ? "border-neutral-900 bg-neutral-50"
                        : "border-neutral-200 hover:border-neutral-400")
                    }
                  >
                    <div className="font-medium">{r.label}</div>
                    <div className="text-[10px] text-neutral-500 mt-0.5">{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-neutral-500 mb-1.5">Workspace access</div>
              {ownerWorkspaces.length === 0 ? (
                <div className="text-xs text-neutral-500">
                  All workspaces (default).
                </div>
              ) : (
                <>
                  <label className="flex items-center gap-2 text-sm cursor-pointer py-1 mb-1 border-b border-neutral-200">
                    <input
                      type="checkbox"
                      data-testid="team-invite-workspace-all"
                      checked={inviteWorkspaceIds === null}
                      onChange={(e) => setInviteAllWorkspaces(e.target.checked)}
                    />
                    <span className="font-medium">All workspaces</span>
                    <span className="text-[10px] text-neutral-400">
                      (includes future workspaces you create)
                    </span>
                  </label>
                  <div
                    className="space-y-1 max-h-40 overflow-y-auto"
                    data-testid="team-invite-workspace-list"
                  >
                    {ownerWorkspaces.map((w) => {
                      const checked = inviteWorkspaceIds === null
                        ? true
                        : inviteWorkspaceIds.includes(w.workspace_id);
                      return (
                        <label
                          key={w.workspace_id}
                          className="flex items-center gap-2 text-sm cursor-pointer py-0.5"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            data-testid={`team-invite-workspace-${w.workspace_id}`}
                            onChange={() => toggleInviteWorkspace(w.workspace_id)}
                          />
                          <span className="truncate">{w.name}</span>
                          {w.is_primary ? (
                            <span className="text-[10px] text-neutral-400">primary</span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                  {Array.isArray(inviteWorkspaceIds) && inviteWorkspaceIds.length === 0 ? (
                    <div className="text-[11px] text-amber-600 mt-1" data-testid="team-invite-workspace-none-note">
                      This member will have <strong>no workspace access</strong> until you assign one.
                    </div>
                  ) : (
                    <div className="text-[11px] text-neutral-400 mt-1">
                      Uncheck &ldquo;All workspaces&rdquo; to pick specific ones.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setInviteOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button data-testid="team-invite-send-btn" onClick={onInvite} disabled={sending}>
              {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent data-testid="team-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit team member</DialogTitle>
            <DialogDescription>
              Change {editTarget?.email || "this member"}'s role or workspace access.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-neutral-500 mb-1.5">Role</div>
              <div className="flex gap-2" data-testid="team-edit-role-group">
                {[
                  { id: "admin",  label: "Admin",  desc: "Manage settings, connections, Autopilot" },
                  { id: "member", label: "Member", desc: "Create posts, use library, publish" },
                ].map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setEditRole(r.id)}
                    data-testid={`team-edit-role-${r.id}`}
                    aria-pressed={editRole === r.id}
                    className={
                      "flex-1 text-left rounded-md border p-3 text-xs transition outline-none " +
                      (editRole === r.id
                        ? // Selected: subtle teal-tinted panel (NOT bright
                          // white) + brand-teal border + soft ring/glow so
                          // the state reads clearly against the dark Team
                          // modal chrome. Title uses full-strength text and
                          // description keeps its muted tone; both remain
                          // fully legible over the tint.
                          "border-[color:rgba(27,168,159,0.6)] bg-[color:rgba(27,168,159,0.12)] " +
                          "ring-1 ring-[color:rgba(27,168,159,0.35)] " +
                          "shadow-[0_0_0_3px_rgba(27,168,159,0.08)] text-[color:var(--text-1)]"
                        : "border-neutral-200 hover:border-neutral-400")
                    }
                  >
                    <div className={
                      "font-medium " +
                      (editRole === r.id ? "text-[color:var(--text-1)]" : "")
                    }>{r.label}</div>
                    <div className={
                      "text-[10px] mt-0.5 " +
                      (editRole === r.id
                        ? "text-[color:var(--text-2)]"
                        : "text-neutral-500")
                    }>{r.desc}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-neutral-500 mb-1.5">Workspace access</div>
              {ownerWorkspaces.length === 0 ? (
                <div className="text-xs text-neutral-500">
                  All workspaces (default).
                </div>
              ) : (
                <>
                  <label className="flex items-center gap-2 text-sm cursor-pointer py-1 mb-1 border-b border-neutral-200">
                    <input
                      type="checkbox"
                      data-testid="team-edit-workspace-all"
                      checked={editWorkspaceIds === null}
                      onChange={(e) => setEditAllWorkspaces(e.target.checked)}
                    />
                    <span className="font-medium">All workspaces</span>
                    <span className="text-[10px] text-neutral-400">
                      (includes future workspaces you create)
                    </span>
                  </label>
                  <div
                    className="space-y-1 max-h-40 overflow-y-auto"
                    data-testid="team-edit-workspace-list"
                  >
                    {ownerWorkspaces.map((w) => {
                      const checked = editWorkspaceIds === null
                        ? true
                        : editWorkspaceIds.includes(w.workspace_id);
                      return (
                        <label
                          key={w.workspace_id}
                          className="flex items-center gap-2 text-sm cursor-pointer py-0.5"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            data-testid={`team-edit-workspace-${w.workspace_id}`}
                            onChange={() => toggleEditWorkspace(w.workspace_id)}
                          />
                          <span className="truncate">{w.name}</span>
                          {w.is_primary ? (
                            <span className="text-[10px] text-neutral-400">primary</span>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                  {Array.isArray(editWorkspaceIds) && editWorkspaceIds.length === 0 ? (
                    <div className="text-[11px] text-amber-600 mt-1" data-testid="team-edit-workspace-none-note">
                      This member will have <strong>no workspace access</strong> until you assign one.
                    </div>
                  ) : (
                    <div className="text-[11px] text-neutral-400 mt-1">
                      Uncheck &ldquo;All workspaces&rdquo; to pick specific ones.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTarget(null)} disabled={editSaving}>
              Cancel
            </Button>
            <Button
              data-testid="team-edit-save-btn"
              onClick={onEditSave}
              disabled={editSaving}
            >
              {editSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <DialogContent data-testid="team-remove-dialog">
          <DialogHeader>
            <DialogTitle>Remove team member?</DialogTitle>
            <DialogDescription>
              Remove this team member from your Postmoro workspace? Their content and activity
              in the workspace stay intact.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRemoveTarget(null)}>Cancel</Button>
            <Button
              variant="destructive"
              data-testid="team-remove-confirm-btn"
              onClick={onRemove}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
